import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IdentificationCard, LockKey, ArrowLeft } from '@phosphor-icons/react';
import BarcodeScanner from '../components/BarcodeScanner';
import { kioskApi, kioskSession } from '../utils/kioskSession';
import './KioskLoginPage.css';

// Public, unauthenticated page — a student without a personal phone/tablet
// scans their ID card on a school-owned shared tablet, enters their PIN,
// and lands on a short-lived KioskPortalPage scoped to e-canteen
// self-checkout only. See routers/kiosk.py and auth.py's get_current_user
// for the backend half of this flow.
function KioskLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState('scan'); // scan | pin
  const [scanning, setScanning] = useState(true);
  const [pendingToken, setPendingToken] = useState(null);
  const [studentFirstName, setStudentFirstName] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState(location.state?.message || null);
  const [busy, setBusy] = useState(false);

  const handleScan = async (cardNumber) => {
    setScanning(false);
    setError(null);
    setBusy(true);
    try {
      const result = await kioskApi.scan(cardNumber.trim());
      setPendingToken(result.pending_token);
      setStudentFirstName(result.student_first_name);
      setStep('pin');
    } catch (err) {
      setError(err.message || 'Could not read that card. Please try again.');
      setScanning(true);
    } finally {
      setBusy(false);
    }
  };

  const backToScan = () => {
    setStep('scan');
    setPendingToken(null);
    setPin('');
    setError(null);
    setScanning(true);
  };

  const submitPin = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) return;
    setBusy(true);
    setError(null);
    try {
      const result = await kioskApi.verifyPin(pendingToken, pin);
      kioskSession.set(result);
      navigate('/kiosk/portal');
    } catch (err) {
      setError(err.message || 'Incorrect PIN');
      setPin('');
      if (err.status === 423) {
        // Locked out — the pending scan was invalidated server-side, so a
        // fresh scan (and a fresh 5-attempt budget) is required.
        setTimeout(backToScan, 1500);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kiosk-login-page">
      <div className="kiosk-login-card">
        <div className="kiosk-login-header">
          <h1>Canteen Kiosk</h1>
          <p>Scan your ID card to order food from the canteen.</p>
        </div>

        {step === 'scan' && (
          <div className="kiosk-login-step">
            <IdentificationCard size={40} weight="duotone" className="kiosk-login-icon" />
            <BarcodeScanner active={scanning} onScan={handleScan} manualLabel="Enter card number manually" />
            {busy && <div className="kiosk-login-status">Checking card…</div>}
          </div>
        )}

        {step === 'pin' && (
          <div className="kiosk-login-step">
            <LockKey size={40} weight="duotone" className="kiosk-login-icon" />
            <div className="kiosk-login-greeting">Hi {studentFirstName}! Enter your 4-digit PIN.</div>
            <form onSubmit={submitPin} className="kiosk-pin-form">
              <input
                className="kiosk-pin-input"
                type="password"
                inputMode="numeric"
                autoFocus
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="••••"
              />
              <button className="btn btn-primary" type="submit" disabled={busy || pin.length !== 4}>
                {busy ? 'Checking…' : 'Continue'}
              </button>
            </form>
            <button className="kiosk-login-back" onClick={backToScan} type="button">
              <ArrowLeft size={14} /> Scan a different card
            </button>
          </div>
        )}

        {error && <div className="kiosk-login-error">{error}</div>}
      </div>
    </div>
  );
}

export default KioskLoginPage;
